import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import alertaService from '../../services/Alerta/AlertaService';
import { TagTipo, TagEvento, TagEstado, Codigo, Transportista, esCritico, bordeTarjeta }
    from './alertaVisual';
import MapaAlerta from './MapaAlerta';
import { registrarLectura } from './lecturaAlerta';
import HistorialAperturas from './HistorialAperturas';

/**
 * Pantalla del revisor (Bloque D).
 *
 * Muestra las alertas que le corresponden al usuario de la sesion por su
 * segmentacion, con la respuesta que dejo el operador al atenderlas, y el boton
 * "Revisado" para quien tenga que confirmarlas.
 *
 * El backend ya filtra por usuario: esta pantalla nunca pide alertas de otro.
 *
 * Se mira sobre todo desde el celular y la tablet, asi que abajo de 768px la
 * grilla se reemplaza por tarjetas. Una tabla de once columnas en un telefono
 * obliga a scrollear de costado para ver el transporte, que es justo uno de los
 * datos que se busca de un vistazo.
 */

/** true cuando la pantalla es angosta. Se recalcula si el usuario rota el equipo. */
const useEsAngosto = (limite = 768) => {
    const consulta = `(max-width: ${limite}px)`;
    const [angosto, setAngosto] = useState(
        () => (typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia(consulta).matches : false)
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(consulta);
        const alCambiar = (e) => setAngosto(e.matches);
        // Safari viejo no tiene addEventListener sobre MediaQueryList.
        if (mq.addEventListener) mq.addEventListener('change', alCambiar);
        else mq.addListener(alCambiar);
        setAngosto(mq.matches);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', alCambiar);
            else mq.removeListener(alCambiar);
        };
    }, [consulta]);
    return angosto;
};

const MisAlertas = () => {

    const toast = useRef(null);
    const esAngosto = useEsAngosto();

    const [alertas, setAlertas] = useState([]);
    const [cargando, setCargando] = useState(false);
    // Si se llego desde la tarjeta "Por revisar" del tablero, arranca filtrado.
    const [soloPendientes, setSoloPendientes] = useState(
        (window.location.hash || '').includes('soloPendientes=1'));
    const [busqueda, setBusqueda] = useState('');
    const [detalle, setDetalle] = useState(null);

    const cargar = useCallback(async (pendientes) => {
        setCargando(true);
        try {
            const data = await alertaService.misAlertas(pendientes, 200);
            setAlertas(Array.isArray(data) ? data : []);
        } catch (e) {
            setAlertas([]);
            toast.current?.show({
                severity: 'error', summary: 'No se pudieron cargar las alertas',
                detail: 'Reintentá en unos segundos.', life: 4000
            });
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(soloPendientes); }, [cargar, soloPendientes]);

    /**
     * Busqueda por placa, candado, booking y contenedor -- los cuatro datos con
     * los que el revisor identifica una unidad.
     *
     * Se filtra en el cliente y no en el servidor porque el backend ya acota a
     * 200 filas: traer eso y filtrar en memoria es instantaneo y evita una
     * consulta por cada tecla.
     */
    const filtradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return alertas;
        return alertas.filter((a) =>
            [a.placa, a.dispositivo, a.booking, a.contenedor, a.chofer, a.empresa]
                .some((v) => (v || '').toString().toLowerCase().includes(q))
        );
    }, [alertas, busqueda]);

    const porRevisar = useMemo(
        () => alertas.filter((a) => a.requiereConfirmacion && !a.revisada).length,
        [alertas]
    );
    const noLeidas = useMemo(() => alertas.filter((a) => !a.leida).length, [alertas]);

    const marcarRevisada = async (a) => {
        try {
            const res = await alertaService.marcarRevisada(a.id);
            if (res?.actualizada) {
                setAlertas((prev) => prev.map((x) =>
                    x.id === a.id ? { ...x, revisada: true, leida: true } : x));
                toast.current?.show({
                    severity: 'success', summary: 'Marcada como revisada', life: 2500
                });
            } else {
                // El backend devuelve false si ya estaba revisada o si este usuario
                // no tiene que confirmarla. No es un error.
                toast.current?.show({
                    severity: 'info', summary: 'Sin cambios',
                    detail: 'Esta alerta ya estaba revisada o no requiere tu confirmación.',
                    life: 3500
                });
            }
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'No se pudo marcar', life: 4000
            });
        }
    };

    /**
     * Abre el detalle y registra la apertura (auditoria: fecha, veces, origen,
     * IP y GPS). SIEMPRE se registra, aunque ya estuviera leida: cada apertura
     * cuenta. Lo que se pinta despues es lo que devolvio el servidor, no un
     * "leida" optimista sin fecha -- asi se veia "Leída · pendiente".
     */
    const abrirDetalle = (a) => {
        setDetalle(a);
        registrarLectura([a.monitoreoDetId], 'MIS_ALERTAS', { avisoId: a.id }).then((por) => {
            const x = por && por[String(a.monitoreoDetId)];
            if (!x) return;
            const cambio = {
                leida: a.leida || !!x.fechaLeida,
                fechaLeida: x.fechaLeida || a.fechaLeida,
                vistas: x.vistas,
                ultimaVista: x.ultimaVista
            };
            setAlertas((prev) => prev.map((y) => (y.id === a.id ? { ...y, ...cambio } : y)));
            setDetalle((d) => (d && d.id === a.id ? { ...d, ...cambio } : d));
        });
    };

    const fecha = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toLocaleString('es-EC');
    };

    const hora = (v) => {
        if (!v) return '';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-EC',
            { hour: '2-digit', minute: '2-digit' });
    };

    const colEstado = (a) => (
        <div className="flex gap-1 flex-wrap">
            {!a.leida && <Tag value="Sin leer" severity="info" />}
            {a.requiereConfirmacion && !a.revisada && <Tag value="Por revisar" severity="warning" />}
            {a.revisada && <Tag value="Revisada" severity="success" />}
            {a.porRespaldo && <Tag value="Por respaldo" severity="danger"
                                   title="No matcheó ninguna suscripción: falta configurar el destinatario" />}
        </div>
    );

    const colAcciones = (a) => (
        <div className="flex gap-2">
            <Button icon="pi pi-eye" className="p-button-text p-button-sm"
                    tooltip="Ver detalle" onClick={() => abrirDetalle(a)} />
            {/* El boton solo existe para quien debe confirmar. Para el resto no
                aparece: no tiene sentido ofrecer una accion que no le toca. */}
            {a.requiereConfirmacion && !a.revisada && (
                <Button label="Revisado" icon="pi pi-check"
                        className="p-button-sm p-button-warning"
                        onClick={() => marcarRevisada(a)} />
            )}
        </div>
    );


    /** Una alerta como tarjeta, para celular y tablet. */
    const tarjeta = (a) => (
        <button key={a.id} type="button" onClick={() => abrirDetalle(a)}
                className="w-full text-left p-3 border-1 border-round surface-card cursor-pointer"
                style={{
                    borderColor: 'var(--surface-border)',
                    borderLeft: `3px solid ${bordeTarjeta(a)}`
                }}>
            <div className="flex align-items-center gap-2 flex-wrap mb-2">
                <TagTipo tipo={a.tipoEvento} />
                {!a.leida && <Tag value="Sin leer" severity="info" />}
                {a.requiereConfirmacion && !a.revisada && <Tag value="Por revisar" severity="warning" />}
                {a.revisada && <Tag value="Revisada" severity="success" />}
                {/* Dice "Evento" a la vista: en el celular no hay hover que
                    aclare cual de las dos fechas es, y entre que el evento
                    ocurre y entra a OPEM pasan 37 minutos en promedio. */}
                <span className="ml-auto text-sm text-color-secondary text-right">
                    <span className="block text-xs uppercase" style={{ opacity: .75 }}>Evento</span>
                    {hora(a.fechaEvento)}
                </span>
            </div>

            <div className="font-semibold mb-2">
                <Transportista nombre={a.empresa} origen={a.transportistaOrigen} />
            </div>

            <div className="flex flex-wrap gap-3 mb-2">
                {[['Placa', a.placa], ['Contenedor', a.contenedor], ['Booking', a.booking]]
                    .map(([k, v]) => (
                        <div key={k}>
                            <div className="text-xs text-color-secondary uppercase">{k}</div>
                            <div className="font-medium"><Codigo valor={v} /></div>
                        </div>
                    ))}
            </div>

            {/* Una alerta puede estar atendida y tipificada sin texto libre: la
                novedad sola ya es la respuesta del operador. */}
            <div className="text-sm p-2 border-round surface-100">
                {(a.novedad || a.observacion)
                    ? <span>
                        {a.novedad && <span className="mr-2"><TagEvento evento={a.novedad} /></span>}
                        {a.observacion}
                      </span>
                    : <span className="text-color-secondary">El operador todavía no respondió</span>}
            </div>

            {a.requiereConfirmacion && !a.revisada && (
                <div className="mt-2">
                    <Button label="Revisado" icon="pi pi-check"
                            className="p-button-sm p-button-warning w-full"
                            onClick={(e) => { e.stopPropagation(); marcarRevisada(a); }} />
                </div>
            )}
        </button>
    );

    /**
     * La bitacora de la alerta: cuando se entrego, cuando se leyo, cuando y
     * quien la reviso. Es la evidencia de la atencion.
     */
    const bitacora = (a) => {
        const pasos = [
            { t: 'Alerta generada', m: fecha(a.fechaEvento), hecho: true },
            { t: 'Entregada', m: fecha(a.fechaRecibida), hecho: !!a.fechaRecibida },
            {
                t: a.leida ? 'Leída' : 'Sin leer',
                m: a.fechaLeida ? fecha(a.fechaLeida) : (a.leida ? 'registrando…' : 'pendiente'),
                hecho: !!a.leida
            }
        ];
        // Cuantas veces la abrio este usuario (auditoria). Solo si el servidor lo
        // informo: sin la tabla de vistas el paso no aparece en vez de mentir.
        if (a.vistas) {
            pasos.push({
                t: a.vistas === 1 ? 'Abierta 1 vez' : `Abierta ${a.vistas} veces`,
                m: a.ultimaVista ? `última: ${fecha(a.ultimaVista)}` : '',
                hecho: true
            });
        }
        if (a.requiereConfirmacion || a.revisada) {
            pasos.push({
                t: a.revisada ? 'Revisada' : 'Revisado pendiente',
                m: a.revisada
                    ? `${fecha(a.fechaRevisada)}${a.usuarioRevisa ? ' · ' + a.usuarioRevisa : ''}`
                    : 'requiere tu confirmación',
                hecho: !!a.revisada
            });
        }
        return (
            <>
                <ul className="list-none p-0 m-0">
                    {pasos.map((p) => (
                        <li key={p.t} className="flex align-items-baseline gap-2 mb-2">
                            <i className={`pi ${p.hecho ? 'pi-check-circle text-green-600'
                                : 'pi-circle text-color-secondary'}`} style={{ fontSize: '.8rem' }} />
                            <span className={p.hecho ? 'font-medium' : 'text-color-secondary'}>{p.t}</span>
                            <span className="text-sm text-color-secondary ml-auto">{p.m}</span>
                        </li>
                    ))}
                </ul>
                {/* key: al abrir otra alerta el historial arranca cerrado. */}
                <HistorialAperturas key={a.monitoreoDetId} monitoreoDetId={a.monitoreoDetId} vistas={a.vistas} />
            </>
        );
    };

    const encabezado = (
        <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <div className="flex align-items-center gap-3 flex-wrap">
                <span className="text-xl font-bold">Mis alertas</span>
                {noLeidas > 0 && <Tag value={`${noLeidas} sin leer`} severity="info" />}
                {porRevisar > 0 && <Tag value={`${porRevisar} por revisar`} severity="warning" />}
            </div>
            <div className="flex align-items-center gap-3 flex-wrap">
                <div className="flex align-items-center gap-2">
                    <InputSwitch inputId="soloPendientes" checked={soloPendientes}
                                 onChange={(e) => setSoloPendientes(e.value)} />
                    <label htmlFor="soloPendientes" className="text-sm">Solo sin leer</label>
                </div>
                <span className="p-input-icon-left flex-auto">
                    <i className="pi pi-search" />
                    {/* Sin ancho fijo: en un telefono 20rem desbordan la fila. */}
                    <InputText id="buscaAlerta" value={busqueda} className="w-full"
                               onChange={(e) => setBusqueda(e.target.value)}
                               placeholder="Placa, candado, booking o contenedor"
                               style={{ minWidth: '14rem' }} />
                </span>
                <Button icon="pi pi-refresh" className="p-button-text"
                        tooltip="Actualizar" onClick={() => cargar(soloPendientes)} />
            </div>
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12">
                <div className="card">
                    {encabezado}

                    {esAngosto ? (
                        <div className="flex flex-column gap-2 mt-3">
                            {cargando && <div className="text-color-secondary">Cargando…</div>}
                            {!cargando && filtradas.length === 0 &&
                                <div className="text-color-secondary">No hay alertas para mostrar.</div>}
                            {filtradas.map(tarjeta)}
                        </div>
                    ) : (
                        <DataTable value={filtradas} loading={cargando} className="mt-3"
                                   paginator rows={20} rowsPerPageOptions={[20, 50, 100]}
                                   emptyMessage="No hay alertas para mostrar."
                                   rowClassName={(a) => `${!a.leida ? 'font-bold ' : ''}${esCritico(a.novedad) || esCritico(a.evento) ? 'bg-red-50' : ''}`}
                                   responsiveLayout="scroll" dataKey="id">
                            {/* "Fecha" a secas se lee como "cuando me llego", y no lo es:
                                medido sobre 4.361 alertas de dos dias, entre que el evento
                                ocurre y entra a OPEM pasan 37 minutos en promedio y hasta
                                un dia y 16 horas. La cadena completa esta en la bitacora
                                del detalle. */}
                            <Column field="fechaEvento" header="Fecha del evento"
                                    body={(a) => fecha(a.fechaEvento)}
                                    sortable style={{ minWidth: '11rem' }} />
                            <Column field="tipoEvento" header="Tipo" sortable style={{ minWidth: '8rem' }}
                                    body={(a) => <TagTipo tipo={a.tipoEvento} />} />
                            <Column field="evento" header="Evento" sortable style={{ minWidth: '12rem' }}
                                    body={(a) => <TagEvento evento={a.evento} />} />
                            <Column field="placa" header="Placa" sortable style={{ minWidth: '7rem' }}
                                    body={(a) => <Codigo valor={a.placa} />} />
                            <Column field="dispositivo" header="Candado" sortable style={{ minWidth: '8rem' }}
                                    body={(a) => <Codigo valor={a.dispositivo} />} />
                            <Column field="booking" header="Booking" sortable style={{ minWidth: '10rem' }}
                                    body={(a) => <Codigo valor={a.booking} />} />
                            <Column field="contenedor" header="Contenedor" sortable style={{ minWidth: '10rem' }}
                                    body={(a) => <Codigo valor={a.contenedor} />} />
                            <Column field="empresa" header="Transporte" sortable style={{ minWidth: '10rem' }}
                                    body={(a) => <Transportista nombre={a.empresa}
                                                                origen={a.transportistaOrigen} />} />
                            <Column field="novedad" header="Respuesta del operador" sortable
                                    style={{ minWidth: '13rem' }}
                                    body={(a) => <TagEvento evento={a.novedad} />} />
                            <Column header="Estado" body={colEstado} style={{ minWidth: '10rem' }} />
                            <Column header="" body={colAcciones} style={{ minWidth: '11rem' }} />
                        </DataTable>
                    )}
                </div>
            </div>

            <Dialog visible={!!detalle} onHide={() => setDetalle(null)} header="Detalle de la alerta"
                    style={{ width: '46rem', maxWidth: '96vw' }} dismissableMask>
                {detalle && (
                    <div className="grid">
                        <div className="col-12 flex align-items-center gap-2 flex-wrap pb-3 border-bottom-1"
                             style={{ borderColor: 'var(--surface-border)' }}>
                            <TagTipo tipo={detalle.tipoEvento} />
                            <TagEvento evento={detalle.novedad || detalle.evento} />
                            <TagEstado estado={detalle.estadoAlerta} />
                            <span className="ml-auto text-sm text-color-secondary">
                                {fecha(detalle.fechaEvento)}
                            </span>
                        </div>

                        {[
                            ['Placa', <Codigo valor={detalle.placa} />],
                            ['Candado', <Codigo valor={detalle.dispositivo} />],
                            ['Booking', <Codigo valor={detalle.booking} />],
                            ['Contenedor', <Codigo valor={detalle.contenedor} />],
                            ['Transporte', <Transportista nombre={detalle.empresa}
                                                          origen={detalle.transportistaOrigen} />],
                            ['Chofer', detalle.chofer || '—']
                        ].map(([k, v]) => (
                            <React.Fragment key={k}>
                                <div className="col-5 md:col-4 text-color-secondary">{k}</div>
                                <div className="col-7 md:col-8">{v}</div>
                            </React.Fragment>
                        ))}

                        {/* De donde salio el transporte. Solo se muestra cuando NO vino
                            de la bitacora, que es el caso en que el nombre puede ser una
                            bolsa y no un transportista real. */}
                        {detalle.transportistaOrigen && detalle.transportistaOrigen !== 'BITACORA' && (
                            <div className="col-12">
                                <small className="text-color-secondary">
                                    El transporte sale de {detalle.transportistaOrigen === 'VIAJE'
                                        ? 'la empresa del viaje' : 'ningún origen'}, no de la bitácora.
                                </small>
                            </div>
                        )}

                        <div className="col-12 mt-3">
                            <div className="font-bold mb-2">Dónde ocurrió</div>
                            <MapaAlerta lat={detalle.latitud} lng={detalle.longitud}
                                        puntoCercano={detalle.puntoCercano}
                                        titulo={detalle.empresa}
                                        evento={detalle.novedad || detalle.evento} />
                        </div>

                        {/* Lo que respondio el operador al atender: es el dato por el
                            que el revisor entra a esta pantalla. */}
                        <div className="col-12 mt-3">
                            <div className="font-bold mb-2">Respuesta del operador</div>
                            <div className="p-3 border-round surface-100">
                                <div><b>Novedad:</b> {detalle.novedad || '— sin tipificar —'}</div>
                                <div className="mt-2"><b>Observación:</b>{' '}
                                    {detalle.observacion || 'sin texto del operador'}</div>

                            </div>
                        </div>

                        <div className="col-12 mt-3">
                            <div className="font-bold mb-2">Bitácora de la alerta</div>
                            <div className="p-3 border-round surface-100">{bitacora(detalle)}</div>
                        </div>

                        {detalle.requiereConfirmacion && !detalle.revisada && (
                            <div className="col-12 mt-3 text-right">
                                <Button label="Marcar como revisada" icon="pi pi-check"
                                        className="p-button-warning"
                                        onClick={() => { marcarRevisada(detalle); setDetalle(null); }} />
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default MisAlertas;
